import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { CheckIcon, StarIcon, ChevronRightIcon, ClockIcon, UsersIcon, VideoIcon } from 'lucide-react';
import { useWebinarSlot } from '../components/useWebinarSlot';
// Calculates the next webinar slot today (10:00, 13:00, 20:00) or tomorrow 10:00
function getNextSlot(now: Date): Date {
  const slots = [10, 13, 20];
  for (const h of slots) {
    const slot = new Date(now);
    slot.setHours(h, 0, 0, 0);
    if (slot.getTime() > now.getTime()) return slot;
  }
  // After 20:00 -> tomorrow 10:00
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(10, 0, 0, 0);
  return tomorrow;
}
function formatSlotLabel(slot: Date, now: Date): string {
  const sameDay = slot.getDate() === now.getDate() && slot.getMonth() === now.getMonth() && slot.getFullYear() === now.getFullYear();
  const hh = slot.getHours().toString().padStart(2, '0');
  return `${sameDay ? 'vandaag' : 'morgen'} om ${hh}:00`;
}
function spotsForMinutes(minutes: number): number {
  // 9 spots at 3hr+, decreasing by 1 every ~30 min, min 2
  const s = Math.floor(minutes / 30) + 3;
  return Math.max(2, Math.min(9, s));
}
function CountdownPlekken() {
  const [now, setNow] = useState<Date>(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const slot = getNextSlot(now);
  const diff = Math.max(0, slot.getTime() - now.getTime());
  const totalSec = Math.floor(diff / 1000);
  const hh = Math.floor(totalSec / 3600);
  const mm = Math.floor(totalSec % 3600 / 60);
  const ss = totalSec % 60;
  const minutesUntil = Math.floor(diff / 60000);
  const spots = spotsForMinutes(minutesUntil);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return <div className="relative mt-2" data-id="element-1085">
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-300 rounded-xl p-5 shadow-sm" data-id="element-1086">
        <div className="flex items-center justify-center gap-2 mb-4 pb-4 border-b border-amber-300" data-id="element-1087">
          <VideoIcon className="w-5 h-5 text-blue-900 flex-shrink-0" data-id="element-1088" />
          <div className="text-center leading-tight" data-id="element-1089">
            <div className="text-[10px] sm:text-[11px] uppercase tracking-wide text-blue-900/70 font-semibold" data-id="element-1090">
              Gratis webinar
            </div>
            <div className="text-sm sm:text-base font-bold text-blue-900" data-id="element-1091">
              Probleemgedrag bij honden
            </div>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6" data-id="element-1092">
          <div className="flex items-center gap-2" data-id="element-1093">
            <ClockIcon className="w-5 h-5 text-amber-700" data-id="element-1094" />
            <div className="text-center" data-id="element-1095">
              <div className="text-[11px] uppercase tracking-wide text-amber-800 font-semibold" data-id="element-1096">
                Volgende sessie {formatSlotLabel(slot, now)}
              </div>
              <div className="text-2xl sm:text-3xl font-bold text-gray-900 tabular-nums leading-tight" aria-live="polite" data-id="element-1097">
                {pad(hh)}:{pad(mm)}:{pad(ss)}
              </div>
            </div>
          </div>

          <div className="hidden sm:block w-px h-12 bg-amber-300" data-id="element-1098" />

          <div className="flex items-center gap-2" data-id="element-1099">
            <UsersIcon className="w-5 h-5 text-red-600" data-id="element-1100" />
            <div className="text-center" data-id="element-1101">
              <div className="text-[11px] uppercase tracking-wide text-red-700 font-semibold" data-id="element-1102">
                Nog beschikbaar
              </div>
              <div className="text-2xl sm:text-3xl font-bold text-red-700 leading-tight" data-id="element-1103">
                <span className="tabular-nums" data-id="element-1104">{spots}</span>{' '}
                <span className="text-base font-semibold" data-id="element-1105">plekken</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Playful hand-drawn arrow pointing to the button below */}
      <div className="absolute left-1/2 top-full -translate-x-1/2 pointer-events-none select-none" aria-hidden="true" data-id="element-1106">
        <div className="relative flex flex-col items-center pt-1" data-id="element-1107">
          <span className="text-amber-700 font-bold text-lg whitespace-nowrap" style={{
          fontFamily: "'Caveat', 'Comic Sans MS', cursive",
          transform: 'rotate(-6deg)'
        }} data-id="element-1108">
            klik hier!
          </span>
          <svg width="44" height="44" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-amber-600 animate-bounce -mt-1" style={{
          animationDuration: '1.6s'
        }} data-id="element-1109">
            <path d="M 28 4 C 14 14, 42 26, 28 38" stroke="currentColor" strokeWidth="3" strokeLinecap="round" fill="none" data-id="element-1110" />
            <path d="M 20 32 L 28 42 L 36 32" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" data-id="element-1111" />
          </svg>
        </div>
      </div>
    </div>;
}
function StickyCountdownBar({
  ctaHref
}: {
  ctaHref: string;
}) {
  const [now, setNow] = useState<Date>(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const slot = getNextSlot(now);
  const diff = Math.max(0, slot.getTime() - now.getTime());
  const totalSec = Math.floor(diff / 1000);
  const hh = Math.floor(totalSec / 3600);
  const mm = Math.floor(totalSec % 3600 / 60);
  const ss = totalSec % 60;
  const minutesUntil = Math.floor(diff / 60000);
  const spots = spotsForMinutes(minutesUntil);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return <div className="fixed bottom-0 left-0 right-0 z-50 bg-gradient-to-br from-amber-50 to-orange-50 border-t-2 border-amber-300 shadow-[0_-4px_12px_rgba(0,0,0,0.08)]" data-id="element-1112">
      <div className="max-w-6xl mx-auto px-3 sm:px-4 py-2.5 sm:py-3 flex items-center justify-center gap-3 sm:gap-5" data-id="element-1113">
        {/* Webinar label */}
        <div className="hidden md:flex items-center gap-2 pr-4 border-r border-amber-300" data-id="element-1114">
          <VideoIcon className="w-5 h-5 text-blue-900 flex-shrink-0" data-id="element-1115" />
          <div className="leading-tight" data-id="element-1116">
            <div className="text-[10px] uppercase tracking-wide text-blue-900/70 font-semibold" data-id="element-1117">
              Gratis webinar
            </div>
            <div className="text-sm font-bold text-blue-900 leading-tight" data-id="element-1118">
              Probleemgedrag bij honden
            </div>
          </div>
        </div>

        {/* Countdown + spots tightly grouped */}
        <div className="flex items-center gap-3 sm:gap-4" data-id="element-1119">
          <div className="flex items-center gap-1.5" data-id="element-1120">
            <ClockIcon className="w-4 h-4 sm:w-5 sm:h-5 text-amber-700 flex-shrink-0" data-id="element-1121" />
            <div className="leading-tight" data-id="element-1122">
              <div className="text-[9px] sm:text-[10px] uppercase tracking-wide text-amber-800 font-semibold" data-id="element-1123">
                Volgende sessie
              </div>
              <div className="text-base sm:text-xl font-bold text-gray-900 tabular-nums leading-tight" aria-live="polite" data-id="element-1124">
                {pad(hh)}:{pad(mm)}:{pad(ss)}
              </div>
            </div>
          </div>

          <div className="w-px h-9 bg-amber-300 flex-shrink-0" data-id="element-1125" />

          <div className="flex items-center gap-1.5" data-id="element-1126">
            <UsersIcon className="w-4 h-4 sm:w-5 sm:h-5 text-red-600 flex-shrink-0" data-id="element-1127" />
            <div className="leading-tight" data-id="element-1128">
              <div className="text-[9px] sm:text-[10px] uppercase tracking-wide text-red-700 font-semibold" data-id="element-1129">
                Nog beschikbaar
              </div>
              <div className="text-base sm:text-xl font-bold text-red-700 leading-tight whitespace-nowrap" data-id="element-1130">
                <span className="tabular-nums" data-id="element-1131">{spots}</span>{' '}
                <span className="text-xs sm:text-sm font-semibold" data-id="element-1132">
                  plekken
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <Link to={ctaHref} className="flex-shrink-0 bg-green-600 hover:bg-green-700 text-white text-center font-bold text-xs sm:text-sm px-3 sm:px-5 py-2.5 sm:py-3 rounded-full shadow-sm transition-colors whitespace-nowrap" data-id="element-1133">
          Gratis Aanmelden →
        </Link>
      </div>
    </div>;
}
export function ArtikelProbleemgedragAdv4VarKortPage() {
  const slotLabel = useWebinarSlot();
  const ctaHref = '/webinar-mc-adv4-pmax';
  const trustboxRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if ((window as any).Trustpilot && trustboxRef.current) {
      ;
      (window as any).Trustpilot.loadFromElement(trustboxRef.current, true);
    }
  }, []);
  const CtaButton = ({
    children,
    className = ''
  }: {
    children: React.ReactNode;
    className?: string;
  }) => <Link to={ctaHref} className={`inline-block bg-green-600 hover:bg-green-700 text-white text-center font-semibold py-4 px-8 rounded-md shadow-sm transition-colors ${className}`} data-id="element-1134">
      {children}
    </Link>;
  return <div className="min-h-screen bg-white text-gray-900" style={{
    fontFamily: "'League Spartan', sans-serif"
  }} data-id="element-1135">
      {/* Top navy banner */}
      <div className="bg-blue-900 text-white text-center py-3 text-sm font-medium tracking-wide" data-id="element-1136">
        Stressless Dogs - Online Magazine
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8 lg:py-10 pb-32" data-id="element-1137">
        {/* Trustpilot row */}
        <div className="flex justify-center mb-6" data-id="element-1138">
          <div ref={trustboxRef} className="trustpilot-widget" data-locale="nl-NL" data-template-id="5419b6ffb0d04a076446a9af" data-businessunit-id="65017f0060003ffc450ac45e" data-style-height="20px" data-style-width="100%" data-theme="light" data-id="element-1139">
            <a href="https://nl.trustpilot.com/review/stresslessdogs.nl" target="_blank" rel="noopener noreferrer" data-id="element-1140">
              Trustpilot
            </a>
          </div>
        </div>

        <div className="lg:grid lg:grid-cols-3 lg:gap-10" data-id="element-1141">
          <article className="lg:col-span-2" data-id="element-1142">
            {/* Breadcrumb */}
            <nav className="text-sm text-gray-500 mb-4 flex items-center gap-1" data-id="element-1143">
              <span className="font-semibold text-gray-700" data-id="element-1144">Home</span>
              <ChevronRightIcon className="w-3 h-3" data-id="element-1145" />
              <span className="font-semibold text-gray-700" data-id="element-1146">Trending</span>
              <ChevronRightIcon className="w-3 h-3" data-id="element-1147" />
              <span data-id="element-1148">Hondengedrag</span>
            </nav>

            {/* H1 */}
            <h1 className="text-3xl lg:text-4xl font-bold leading-tight mb-6" data-id="element-1149">
              Trekt, blaft, valt uit of negeert jouw hond je? Dit is waarom - en
              het ligt níet aan jou of je hond.
            </h1>

            {/* Subhead intro */}
            <p className="text-lg text-gray-700 leading-relaxed mb-6" data-id="element-1150">
              Een Nederlandse gedragsdeskundige legt in een gratis online
              presentatie uit waarom <strong data-id="element-1151">72% van de baasjes</strong> denkt
              dat ze hun hond begrijpen - terwijl ze dagelijks cruciale signalen
              missen. Inmiddels volgden ruim{' '}
              <strong data-id="element-1152">100.000 hondenbaasjes</strong> deze presentatie.
            </p>

            {/* Hero figure */}
            <figure className="mb-8" data-id="element-1153">
              <img src="https://cdn.magicpatterns.com/uploads/w1qPqfcjJLeufAVhwWekKr/Scherm­afbeelding_2026-05-11_om_13.20.43.png" alt="Hondentrainer legt gedrag uit met whiteboard-tekeningen" className="w-full rounded-sm" data-id="element-1154" />
            </figure>

            <div className="space-y-5 text-[17px] leading-relaxed text-gray-800" data-id="element-1155">
              <p data-id="element-1156">
                <strong data-id="element-1157">Herken je dit?</strong> Je hond blaft om niks. Trekt aan
                de riem. Valt uit naar andere honden. Springt op tegen bezoek.
                En als je iets zegt, lijkt het alsof hij je niet hoort.
              </p>

              <p data-id="element-1158">
                Het frustreert je. En het ergste? Je hebt het gevoel dat je iets
                verkeerd doet.
              </p>

              <p data-id="element-1159">
                Maar het probleem ligt niet bij jou. En ook niet bij je hond.
              </p>

              <h2 className="text-2xl font-bold mt-8 mb-3" data-id="element-1160">
                Jullie spreken twee verschillende talen
              </h2>

              <p data-id="element-1161">
                Honden communiceren voor <strong data-id="element-1162">95%</strong> met hun lichaam en
                maar voor 5% met geluid. Bij mensen is dat precies andersom.
              </p>

              <p data-id="element-1163">
                Het gevolg: jij zegt met je mond "stop", maar je hond ziet met
                zijn ogen iets heel anders. De hele dag door, zonder dat je het
                doorhebt.
              </p>

              <p data-id="element-1164">
                Een voorbeeld. Je hond springt op tegen bezoek. Jij duwt hem weg
                en roept "Laag!" Maar voor een opgewonden hond is wegduwen en
                roepen óók aandacht. Hij denkt niet: "ik word gestraft." Hij
                denkt: "kijk, mijn baasje doet mee."
              </p>

              <p data-id="element-1165">
                En zo bouwt jouw hond keer op keer stress op. Hij voelt dat je
                boos bent, maar snapt niet waarom. Resultaat: meer blaffen, meer
                uitvallen, meer angst.
              </p>

              <figure className="my-6" data-id="element-1166">
                <img src="https://cdn.magicpatterns.com/uploads/64fjzy18K6oPyFW36hTnVW/Scherm­afbeelding_2026-05-11_om_12.39.25.png" alt="Eigenaar staat gefrustreerd tegenover hond in woonkamer" className="w-full rounded-sm" data-id="element-1167" />
              </figure>

              <h2 className="text-2xl font-bold mt-8 mb-3" data-id="element-1168">
                De alfa-mythe houdt het probleem in stand
              </h2>

              <p data-id="element-1169">
                Decennialang werd gezegd dat je "de baas" moest zijn. Maar{' '}
                <strong data-id="element-1170">Dr. David L. Mech</strong> - de onderzoeker achter het
                alfa-concept - heeft zelf afstand genomen van die theorie. En
                recent onderzoek (<em data-id="element-1171">Casey et al., 2014</em>) laat zien dat
                dominantie-trainingen juist <strong data-id="element-1172">méér</strong>{' '}
                gedragsproblemen veroorzaken.
              </p>

              <p data-id="element-1173">
                Gedragsdeskundige <strong data-id="element-1174">Amee de Bruijn</strong>, oprichtster
                van Stressless Dogs, koos een andere weg. Ze leert baasjes de
                taal die honden onderling al spreken. Geen dwang. Geen
                correcties. Geen snoepjes als omkoping.
              </p>

              <p data-id="element-1175">
                Inmiddels hielp ze ruim <strong data-id="element-1176">15.000 baasjes</strong>{' '}
                persoonlijk. Volgden <strong data-id="element-1177">100.000+</strong> mensen haar
                gratis online presentatie. En op Trustpilot staan meer dan{' '}
                <strong data-id="element-1178">1.000 5-sterren reviews</strong>.
              </p>

              <figure className="my-6" data-id="element-1179">
                <img src="https://cdn.magicpatterns.com/uploads/tEawgTwN6ANqek5vk3uxJE/Scherm­afbeelding_2026-05-11_om_12.41.32.png" alt="700+ vijf-sterren reviews voor Amee van Stressless Dogs op Trustpilot" className="w-full rounded-sm" data-id="element-1180" />
              </figure>

              <div className="bg-gray-50 rounded-lg p-5 my-6" data-id="element-1181">
                <div className="flex gap-0.5 mb-2" data-id="element-1182">
                  {[0, 1, 2, 3, 4].map(i => <StarIcon key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" data-id="element-1183" />)}
                </div>
                <p className="italic text-gray-700 mb-2" data-id="element-1184">
                  "Ik had al veel hondentrainingen gevolgd, maar mijn hond bleef
                  uitvallen. Met Liefdevol Communiceren begrijp ik nu eindelijk
                  wat ze wél en niet snapt."
                </p>
                <p className="text-sm font-semibold" data-id="element-1185">- R. Bosma</p>
              </div>

              <h2 className="text-2xl font-bold mt-8 mb-3" data-id="element-1186">
                Wat je in 90 minuten leert
              </h2>

              <ul className="space-y-3" data-id="element-1187">
                {[<>
                    De <strong data-id="element-1188">5 basis-signalen</strong> die elke hond gebruikt
                  </>, <>
                    Hoe je <strong data-id="element-1189">stress herkent</strong> vóórdat het escaleert
                  </>, <>
                    Direct toepasbare technieken -{' '}
                    <strong data-id="element-1190">zonder commando's of correcties</strong>
                  </>].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-1191">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-600 text-white flex items-center justify-center mt-0.5" data-id="element-1192">
                      <CheckIcon className="w-4 h-4" data-id="element-1193" />
                    </span>
                    <span data-id="element-1194">{item}</span>
                  </li>)}
              </ul>

              <p className="mt-6" data-id="element-1195">
                Per sessie zijn er <strong data-id="element-1196">75 plekken</strong> beschikbaar. Live
                niet mogelijk? Alle deelnemers krijgen de opname automatisch
                toegestuurd.
              </p>

              <div className="my-8 flex flex-col items-center gap-24" data-id="element-1197">
                <div className="w-full" data-id="element-1198">
                  <CountdownPlekken data-id="element-1199" />
                </div>
                <CtaButton className="w-full sm:w-auto" data-id="element-1200">
                  Bekijk beschikbaarheid online presentatie →
                </CtaButton>
              </div>
            </div>
          </article>

          {/* Sticky sidebar */}
          <aside className="hidden lg:block lg:col-span-1" data-id="element-1201">
            <div className="sticky top-6 bg-white border border-gray-200 rounded-lg p-6 shadow-sm" data-id="element-1202">
              <p className="text-center font-bold text-lg mb-2" data-id="element-1203">Aanrader:</p>
              <div className="flex justify-center gap-0.5 mb-2" data-id="element-1204">
                {[0, 1, 2, 3, 4].map(i => <StarIcon key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" data-id="element-1205" />)}
              </div>
              <p className="text-center text-sm text-gray-600 mb-5" data-id="element-1206">
                <strong data-id="element-1207">4.68</strong> | 1000+ Geverifieerde Reviews
              </p>

              <h3 className="font-bold text-lg leading-tight mb-4 text-center" data-id="element-1208">
                Gratis Online Presentatie: Communiceren met je hond
              </h3>

              <ul className="space-y-3 mb-5" data-id="element-1209">
                {['Waarom je hond blaft, trekt, uitvalt, opspringt of angstig is', 'Wat de nieuwste wetenschap zegt over hondentraining (meeste baasjes weten dit niet)', 'Hoe je gedragsproblemen oplost, zonder straffen of corrigeren', "Hoe je de 'Verborgen Handleiding' van je hond vindt en zo beter samenwerkt dan ooit"].map((item, i) => <li key={i} className="flex items-start gap-2 text-sm" data-id="element-1210">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-green-600 text-white flex items-center justify-center mt-0.5" data-id="element-1211">
                      <CheckIcon className="w-3 h-3" data-id="element-1212" />
                    </span>
                    <span data-id="element-1213">{item}</span>
                  </li>)}
              </ul>

              <p className="text-sm text-center mb-4" data-id="element-1214">
                <strong data-id="element-1215">
                  {slotLabel.dayPrefix} {slotLabel.dateLabel}
                </strong>{' '}
                om <strong data-id="element-1216">{slotLabel.time}</strong>
              </p>

              <div className="rounded-lg overflow-hidden mb-4 aspect-video bg-gray-100" data-id="element-1217">
                <img src="https://cdn.magicpatterns.com/uploads/o5oXcxyFnv65igmDcbYHEc/Scherm­afbeelding_2026-05-11_om_11.52.59.png" alt="Webinar preview" className="w-full h-full object-cover" data-id="element-1218" />
              </div>

              <Link to={ctaHref} className="block w-full bg-green-600 hover:bg-green-700 text-white text-center font-bold py-3 px-6 rounded-full shadow-sm transition-colors" data-id="element-1219">
                Aanmelden
              </Link>
            </div>
          </aside>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-blue-900 text-white py-12 px-4" data-id="element-1220">
        <div className="max-w-4xl mx-auto text-sm leading-relaxed text-center space-y-6" data-id="element-1221">
          <p data-id="element-1222">
            Meta Advertising Disclaimer: Deze website maakt gebruik van Meta
            Pixel voor advertentiedoeleinden. Dit betekent dat informatie over
            uw bezoek aan deze website wordt gedeeld met Meta (Facebook) om
            gerichte advertenties te kunnen tonen. Door gebruik te maken van
            deze website gaat u akkoord met het gebruik van cookies en
            vergelijkbare technologieën voor analytische en
            advertentiedoeleinden. Deze pagina is een advertorial voor
            Stressless Dogs. Stressless Dogs is een onafhankelijk bedrijf en is
            op geen enkele wijze verbonden aan of onderdeel van Meta (voorheen
            Facebook). Deze advertentie wordt getoond via het Meta
            advertentieplatform, maar Meta is niet verantwoordelijk voor de
            inhoud of diensten aangeboden door Stressless Dogs.
          </p>
          <div className="border-t border-blue-800 pt-6" data-id="element-1223">
            <p className="text-xs tracking-wide" data-id="element-1224">
              THIS IS AN ADVERTISEMENT AND NOT AN ACTUAL NEWS ARTICLE, BLOG, OR
              CONSUMER PROTECTION UPDATE
            </p>
          </div>
        </div>
      </footer>

      {/* Sticky bottom bar with countdown + spots + CTA */}
      <StickyCountdownBar ctaHref={ctaHref} data-id="element-1225" />
    </div>;
}